import React, { useState, useRef, useEffect } from 'react';
import { Plus, Minus, ChevronDown } from 'react-feather';
import useLayersStore from '../stores/layersStore';

const Annotation = ({ id, x, y, title, description, visible = true }) => {
  const [showDescription, setShowDescription] = useState(false);
  const [offset, setOffset] = useState({ x: 40, y: 0 });
  const [annotationWidth, setAnnotationWidth] = useState(202); // Default to min width + border
  const dragStart = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const timeoutRef = useRef(null);
  const annotationRef = useRef(null);

  const { expandedAnnotations, setExpandedAnnotation } = useLayersStore();
  const isExpanded = expandedAnnotations.has(id);

  // Measure annotation width when expanded
  useEffect(() => {
    if (isExpanded && annotationRef.current) {
      const width = annotationRef.current.offsetWidth;
      setAnnotationWidth(width);
    }
  }, [isExpanded, title, description]);

  const toggleExpanded = e => {
    if (!isDraggingRef.current) {
      setExpandedAnnotation(id, !isExpanded);
    }
  };

  const handleMouseDown = e => {
    e.stopPropagation();
    dragStart.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
  };

  const handleMouseMove = e => {
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
    }

    setOffset({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleGlobalMouseUp = e => {
    e.stopPropagation();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      isDraggingRef.current = false;
    }, 200);

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  };

  const handleTouchStart = e => {
    e.stopPropagation();
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX - offset.x,
      y: touch.clientY - offset.y,
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleGlobalTouchEnd);
  };

  const handleTouchMove = e => {
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
    }

    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - dragStart.current.x,
      y: touch.clientY - dragStart.current.y,
    });
  };

  const handleGlobalTouchEnd = e => {
    e.stopPropagation();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      isDraggingRef.current = false;
    }, 200);

    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleGlobalTouchEnd);
  };

  return (
    <div
    className='flex flex-col items-center justify-center'
       style={{
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        width: '0px',
        height: '0px',
        pointerEvents: visible ? 'all' : 'none',
      }}
    >
      {isExpanded && (
        <svg
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
            opacity: visible ? 1 : 0.2,
            transition: 'opacity 0.75s ease',
          }}
        >
          <line
            x1={x}
            y1={y}
            x2={x + (offset.x <= -annotationWidth ? offset.x + annotationWidth : offset.x)}
            y2={y + offset.y + 25}
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}

      <div
        onClick={toggleExpanded}
        style={{
          position: 'relative',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: isExpanded ? '#ffffff' : '#4f9dc9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: isExpanded ? '#4f9dc9' : 'white',
          opacity: visible ? 1 : 0.1,
          border: isExpanded ? '2px solid #4f9dc9' : '2px solid white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 10,
        }}
      >
        {isExpanded ? <Minus size={20} className='m-[3px]' /> : <Plus size={20} className='m-[3px]' />}
      </div>

      {isExpanded && (
        <div
          ref={annotationRef}
          className='pointer-events-auto z-50'
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          style={{
            position: 'absolute',
            left: `${offset.x}px`,
            top: `${offset.y}px`,
            backgroundColor: '#4f9dc9',
            padding: '12px',
            borderRadius: '8px',
            color: 'white',
            width: 'min-content',
            minWidth: '200px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            cursor: isDraggingRef.current ? 'grabbing' : 'grab',
            userSelect: 'none',
            opacity: visible ? 1 : 0.2,
            transition: 'opacity 0.75s ease',
            border: '2px solid white',
          }}
        >
          <div
            onClick={e => {
              if (!isDraggingRef.current) {
                setShowDescription(!showDescription);
              }
              e.stopPropagation();
            }}
            style={{
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: showDescription ? '8px' : '0',
              borderBottom: showDescription ? '2px solid white' : 'none',
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>{title}</span>
            <ChevronDown
              size={16}
              style={{
                transform: showDescription ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.75s ease',
                marginLeft: '5px',
              }}
            />
          </div>
          {showDescription && <div className='mt-2' style={{ minWidth: '200px' }}>{description}</div>}
        </div>
      )}
    </div>
  );
};

export default Annotation;
